import { Component, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AnalyticEvents, FileUploadDirectories } from '@app/enums';
import { NavigationGuard } from '@app/guards';
import { EmployeeDocumentUploadable } from '@app/modules/documents-v2/classes/employee-document-uploadable';
import { AnalyticService, AuthService, FileHelperService, NotifyService } from '@app/services';
import { BulkUploadView } from '@common/views/bulk-upload/bulk-upload-view';
import { DocumentAllowedFileTypes } from '@documents-v2/enums';
import { Role } from '@models/account/role.model';
import { EmployeeDocument } from '@models/documents/employee-document.model';
import { Employee } from '@models/employee/employee.model';

@Component({
    templateUrl: './employee-documents-bulk-upload.template.html',
    styleUrls: ['../../../../../../styles/shared-views/documents-bulk-upload.scss'],
})
export class EmployeeDocumentsBulkUploadView extends BulkUploadView {
    uploadLimit = 25;
    uploadDescriptor = 'documents';
    uploadables: EmployeeDocumentUploadable[] = [];
    employee: Employee;
    roles: Role[] = [];
    isSelfUpload = false;
    isAdmin = false;
    hasAcknowledgedRoleWarning = false;
    allowedFileTypes = DocumentAllowedFileTypes.acknowledgement
        .split(',')
        .map((ext) => `.${ext}`)
        .join(',');

    /**
     * Whether this view is being used in the account module
     * When used in the account module, we don't show the employee's name
     */
    isAccountView = false;

    backRoute = '';

    constructor(
        public fileHelperService: FileHelperService,
        public notifyService: NotifyService,
        private analyticService: AnalyticService,
        private route: ActivatedRoute,
        private auth: AuthService,
        private notify: NotifyService
    ) {
        super(fileHelperService, notifyService);
        this.employee = this.route.snapshot.data.models.get('employee');
        this.isSelfUpload = this.employee.is(this.auth.employee);
        this.isAccountView = Boolean(this.route.snapshot.data.isAccountView);
        this.isAdmin = this.auth.isAdmin();
    }

    uploadableCreator = (file: File): EmployeeDocumentUploadable => new EmployeeDocumentUploadable(file);

    @HostListener('window:beforeunload', ['$event'])
    warn($event: BeforeUnloadEvent): void {
        if (this.status === 'uploading') {
            $event.returnValue = NavigationGuard.warningMessage;
        }
    }

    protected async onInit(): Promise<void> {
        this.getRoles();
        this.backRoute = this.isSelfUpload ? '/profile/documents-v2/folders' : '../';
    }

    // Have to implement this method, but they are not needed for employee bulk uploading
    protected async onValidate(): Promise<void> {}

    protected async onReset(): Promise<void> {
        this.analyticService.trackEvent(
            AnalyticEvents[`BulkUploader${this.isSelfUpload ? 'Self' : 'Employee'}ProfileDocumentReset`]
        );
    }

    protected onRemove(uploadable: EmployeeDocumentUploadable): void {
        this.analyticService.trackEvent(
            AnalyticEvents[`BulkUploader${this.isSelfUpload ? 'Self' : 'Employee'}ProfileDocumentRemoved`],
            {
                fileHadError: uploadable.hasInvalidFile(),
            }
        );
    }

    protected onAdded(uploadables: EmployeeDocumentUploadable[]): void {
        uploadables.forEach((uploadable) => {
            if (uploadable.hasInvalidFile()) {
                this.analyticService.trackEvent(
                    AnalyticEvents[`BulkUploader${this.isSelfUpload ? 'Self' : 'Employee'}ProfileDocumentError`],
                    {
                        invalidFileSize: uploadable.isTooLarge,
                        invalidFileType: uploadable.hasWrongFileType,
                    }
                );
            }
        });
    }

    protected async onUpload(): Promise<void> {
        const uploadPromises = this.uploadables.map(async (uploadable) => {
            try {
                const file = await this.fileHelperService.store(uploadable.file, FileUploadDirectories.documents);
                const selectedRoles = this.isSelfUpload ? [] : uploadable.roles;

                const employeeDocument = new EmployeeDocument();
                employeeDocument.fileId = file.id;
                employeeDocument.name = uploadable.getUploadableName();
                employeeDocument.attach('roles', selectedRoles);
                employeeDocument.param('employee', this.employee.id);

                await employeeDocument.save();

                uploadable.status = 'complete';
            } catch (error) {
                uploadable.status = 'failed';
            }
        });

        await Promise.all(uploadPromises);

        const successfullyUploadedFiles = this.uploadables.filter((uploadable) => uploadable.status === 'complete');
        this.analyticService.trackEvent(
            AnalyticEvents[`BulkUploader${this.isSelfUpload ? 'Self' : 'Employee'}ProfileDocumentSaved`],
            { uploadedFileCount: successfullyUploadedFiles.length }
        );
    }

    protected getRoles(): void {
        Role.where('company_id', this.auth.company.id)
            .all()
            .then(([roles, _]: [Role[], unknown]) => {
                this.roles = roles;
            })
            .catch((err) => this.notify.error(err));
    }
}
